import type { AchievementRarity, QuestMetricType } from '../client/generated/graphql';

export const VoiceState = {
    IDLE: 'idle',
    LISTENING: 'listening',
    PROCESSING: 'processing',
    REVIEW: 'review',
    SUCCESS: 'success',
    ERROR: 'error',
} as const;

export type VoiceStateType = (typeof VoiceState)[keyof typeof VoiceState];

export const VoiceActionType = {
    CREATE_QUEST: 'CREATE_QUEST',
    CREATE_CATEGORY: 'CREATE_CATEGORY',
    POSTPONE_QUEST: 'POSTPONE_QUEST',
    CREATE_ACHIEVEMENT: 'CREATE_ACHIEVEMENT',
} as const;

export type VoiceActionTypeValue = (typeof VoiceActionType)[keyof typeof VoiceActionType];

export type VoiceActionSharedFields = {
    id: string;
    dependsOnActionIds?: string[];
    dependsOnActionIndices?: number[];
    displayTitle?: string;
};

export interface CreateQuestVoiceAction extends VoiceActionSharedFields {
    type: 'CREATE_QUEST';
    questName: string;
    description?: string;
    categoryName?: string;
    categoryId?: string;
    dueDateIso?: string;
    difficulty?: string;
    focusModeEnabled?: boolean;
    timedIntervalMinutes?: number;
    repeatQuery?: string;
}

export interface CreateCategoryVoiceAction extends VoiceActionSharedFields {
    type: 'CREATE_CATEGORY';
    categoryName: string;
    description?: string;
    iconName?: string;
    iconColorName?: string;
}

export interface PostponeQuestVoiceAction extends VoiceActionSharedFields {
    type: 'POSTPONE_QUEST';
    questName: string;
    questId?: string;
    dueDateIso?: string;
}

export interface CreateAchievementVoiceAction extends VoiceActionSharedFields {
    type: 'CREATE_ACHIEVEMENT';
    achievementName: string;
    description?: string;
    categoryName?: string;
    categoryId?: string;
    questNames?: string[];
    questIds?: string[];
    icon: string;
    rarity: AchievementRarity;
    conditionMetricType?: QuestMetricType;
    conditionTarget?: number | null;
    conditionQuestIds?: string[];
}

export type VoiceAction =
    | CreateQuestVoiceAction
    | CreateCategoryVoiceAction
    | PostponeQuestVoiceAction
    | CreateAchievementVoiceAction;

export type CreateQuestVoiceActionUpdate = Partial<
    Pick<
        CreateQuestVoiceAction,
        | 'displayTitle'
        | 'questName'
        | 'description'
        | 'categoryName'
        | 'categoryId'
        | 'dueDateIso'
        | 'difficulty'
        | 'focusModeEnabled'
        | 'timedIntervalMinutes'
        | 'repeatQuery'
    >
>;
export type CreateCategoryVoiceActionUpdate = Partial<
    Pick<
        CreateCategoryVoiceAction,
        'displayTitle' | 'categoryName' | 'description' | 'iconName' | 'iconColorName'
    >
>;
export type PostponeQuestVoiceActionUpdate = Partial<
    Pick<PostponeQuestVoiceAction, 'displayTitle' | 'questId' | 'dueDateIso' | 'questName'>
>;
export type CreateAchievementVoiceActionUpdate = Partial<
    Pick<
        CreateAchievementVoiceAction,
        | 'displayTitle'
        | 'achievementName'
        | 'description'
        | 'categoryId'
        | 'categoryName'
        | 'questIds'
        | 'questNames'
        | 'icon'
        | 'rarity'
        | 'conditionMetricType'
        | 'conditionTarget'
        | 'conditionQuestIds'
        | 'dependsOnActionIds'
        | 'dependsOnActionIndices'
    >
>;
export type VoiceActionUpdate =
    | CreateQuestVoiceActionUpdate
    | PostponeQuestVoiceActionUpdate
    | CreateCategoryVoiceActionUpdate
    | CreateAchievementVoiceActionUpdate;
